import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, authHeader } from "@/lib/api";

const prepareForm = (fields) =>
  fields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});

export default function CmsManager({ title, endpoint, fields, token, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(prepareForm(fields));

  const normalizedTitle = useMemo(
    () => title.toLowerCase().replace(/\s+/g, "-"),
    [title],
  );

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/${endpoint}`, authHeader(token));
      setItems(response.data);
    } catch {
      toast.error(`Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [endpoint, token]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const normalizePayload = () => {
    const payload = { ...form };
    if (Object.hasOwn(payload, "authors")) {
      payload.authors = payload.authors
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
    }
    return payload;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = normalizePayload();
      if (editingId) {
        await api.put(`/admin/${endpoint}/${editingId}`, payload, authHeader(token));
        toast.success(`${title} updated`);
      } else {
        await api.post(`/admin/${endpoint}`, payload, authHeader(token));
        toast.success(`${title} created`);
      }
      setEditingId(null);
      setForm(prepareForm(fields));
      loadItems();
    } catch {
      toast.error(`Could not save ${title}`);
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    const mapped = fields.reduce((acc, field) => {
      if (field.name === "authors" && Array.isArray(item.authors)) {
        return { ...acc, authors: item.authors.join(", ") };
      }
      return { ...acc, [field.name]: item[field.name] ?? "" };
    }, {});
    setForm(mapped);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Delete this ${title.slice(0, -1)}?`)) {
      return;
    }
    try {
      await api.delete(`/admin/${endpoint}/${id}`, authHeader(token));
      toast.success(`${title.slice(0, -1)} deleted`);
      loadItems();
    } catch {
      toast.error(`Could not delete ${title.slice(0, -1)}`);
    }
  };

  return (
    <div className="space-y-8" data-testid={testId}>
      <form
        className="grid gap-4 rounded-2xl border border-[#f2e6c9] bg-white p-6"
        onSubmit={onSubmit}
        data-testid={`${normalizedTitle}-form`}
      >
        <h3
          className="font-[Playfair_Display] text-2xl text-[#1a1a1a]"
          data-testid={`${normalizedTitle}-form-title`}
        >
          {editingId ? `Edit ${title.slice(0, -1)}` : `Create ${title.slice(0, -1)}`}
        </h3>

        {fields.map((field) => (
          <div key={field.name} className="space-y-1.5">
            <label
              className="font-[Manrope] text-xs font-semibold uppercase tracking-[0.14em] text-[#8b7a55]"
              data-testid={`${normalizedTitle}-field-label-${field.name}`}
            >
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <Textarea
                value={form[field.name] || ""}
                onChange={(event) => updateField(field.name, event.target.value)}
                placeholder={field.placeholder}
                className="min-h-24"
                data-testid={`${normalizedTitle}-field-input-${field.name}`}
                required={field.required}
              />
            ) : (
              <Input
                type="text"
                value={form[field.name] || ""}
                onChange={(event) => updateField(field.name, event.target.value)}
                placeholder={field.placeholder}
                data-testid={`${normalizedTitle}-field-input-${field.name}`}
                required={field.required}
              />
            )}
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-full bg-[#c5a059] px-6 text-white hover:bg-[#b08d45]"
            data-testid={`${normalizedTitle}-save-button`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
          {editingId && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditingId(null);
                setForm(prepareForm(fields));
              }}
              data-testid={`${normalizedTitle}-cancel-edit-button`}
            >
              Cancel Edit
            </Button>
          )}
        </div>
      </form>

      <div className="space-y-3" data-testid={`${normalizedTitle}-list`}>
        {loading ? (
          <p className="font-[Manrope] text-sm text-[#666666]" data-testid={`${normalizedTitle}-loading-text`}>
            Loading {title}...
          </p>
        ) : (
          items.map((item) => (
            <article
              key={item.id}
              className="rounded-2xl border border-[#f2e6c9] bg-[#fffdf8] p-5"
              data-testid={`${normalizedTitle}-item-${item.id}`}
            >
              <h4
                className="font-[Playfair_Display] text-xl text-[#1a1a1a]"
                data-testid={`${normalizedTitle}-item-title-${item.id}`}
              >
                {item.title}
              </h4>
              <p
                className="mt-2 line-clamp-2 font-[Manrope] text-sm text-[#555555]"
                data-testid={`${normalizedTitle}-item-description-${item.id}`}
              >
                {item.excerpt || item.summary || item.abstract || "No description"}
              </p>
              <div className="mt-4 flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onEdit(item)}
                  className="rounded-full"
                  data-testid={`${normalizedTitle}-edit-button-${item.id}`}
                >
                  <Pencil className="mr-1 h-4 w-4" /> Edit
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => onDelete(item.id)}
                  className="rounded-full"
                  data-testid={`${normalizedTitle}-delete-button-${item.id}`}
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Delete
                </Button>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
